import { useMemo } from "react";

import { useIsFetching, useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { endOfDay, startOfDay } from "date-fns";
import { DateRange } from "react-day-picker";

import { deserializeJsonApi } from "src/api/jsonApiHelpers";
import {
  isFilterKey,
  URLParamFilter,
  useFilter,
} from "src/base-components/tableColumnFilters/useFilters";
import { useUpdateCase } from "src/cases/queries/cases";
import {
  CASE_QUEUES_QUERY_KEY_PREFIX,
  caseQueuesKeys,
  useCaseQueue,
} from "src/cases/queries/queues";
import {
  useCaseTemplatesRaw,
  useCaseTemplateVersion,
} from "src/cases/queries/templates";
import { useQueueMembers } from "src/cases/queues/hooks";
import { DeserializedCaseTemplateWithVersionsData } from "src/cases/queues/types";
import { CaseTemplatesResponse } from "src/clients/case-management";
import { toastActions } from "src/design-system/Toast/utils";
import { useWorkspaceContext } from "src/router/routerContextHooks";
import { CasesPageParams } from "src/router/urls";
import { logger } from "src/utils/logger";
import { isPreconditionError } from "src/utils/predicates";
import { useParamsDecode } from "src/utils/useParamsDecode";

type AssignCaseParams = {
  caseId: string;
  etag: string;
  queueId?: string;
  assigneeId?: string | null;
};

export const useAssignCase = (queueId?: string) => {
  const {
    mutateAsync: updateCase,
    variables,
    isPending,
  } = useUpdateCase(queueId);

  const onAssign = async (params: AssignCaseParams) => {
    try {
      await updateCase({
        id: params.caseId,
        assigneeId: params.assigneeId,
        ifMatch: params.etag,
      });
    } catch (error) {
      logger.error("Error assigning case", error);

      if (isPreconditionError(error)) {
        toastActions.failure({
          title: "Assigning failed",
          description:
            "The case has been updated since you last loaded it. Please refresh the page and try again.",
        });
        return;
      }

      if (isAxiosError(error)) {
        toastActions.failure({
          title: "Assigning failed",
          description:
            error.response?.data.errors[0]?.detail ?? "Unknown error",
        });
        return;
      }

      toastActions.failure({
        title: "Assigning failed",
        description:
          "Unexpected error occurred while assigning the case. Please try again later.",
      });
    }
  };

  return {
    onAssign,
    variables,
    isPending,
  };
};

export const useDateRangeFilter = (property: string) => {
  const { update, filters, reset, updateParams } = useFilter(property, [
    { key: property, operator: "gte", values: [] },
    { key: property, operator: "lte", values: [] },
  ]);

  const apply = (filters: URLParamFilter[]) => {
    updateParams((searchParams) => {
      Array.from(searchParams.keys()).forEach((key) => {
        if (isFilterKey(key) && key.startsWith(`filter[${property}]`)) {
          searchParams.delete(key);
        }
      });

      filters.forEach((filter) => {
        if (filter.values.length > 0) {
          searchParams.set(
            `filter[${filter.key}][${filter.operator}]`,
            filter.values.join(","),
          );
        }
      });

      return searchParams;
    });
  };

  const onDateRangeChange = (value?: DateRange) => {
    if (!value?.from && !value?.to) return;

    const from = startOfDay((value.from ?? value.to) as Date);
    const to = endOfDay((value.to ?? value.from) as Date);

    const newFilters = [
      {
        key: property,
        operator: "gte",
        values: [from.toISOString()],
      },
      {
        key: property,
        operator: "lte",
        values: [to.toISOString()],
      },
    ];

    update(newFilters);
    apply(newFilters);
  };

  const dateRange = useMemo(() => {
    const isFrom = (filter: URLParamFilter) => filter.operator === "gte";
    const isTo = (filter: URLParamFilter) => filter.operator === "lte";

    const from = filters.find(isFrom)?.values?.[0];
    const to = filters.find(isTo)?.values?.[0];

    return {
      from: from ? new Date(from) : undefined,
      to: to ? new Date(to) : undefined,
    };
  }, [filters]);

  return {
    update,
    onDateRangeChange,
    onDateRangeReset: reset,
    dateRange,
  };
};

export enum TemplateStatus {
  Outdated = "Outdated",
  Deleted = "Deleted",
  Active = "Active",
}

export const useTemplateStatus = (params: {
  queueId?: string;
  templateId?: string;
  versionId?: string;
  schemaVersion?: number;
}): { isLoading: boolean; status?: TemplateStatus } => {
  const { queueId, templateId, versionId, schemaVersion } = params;
  const { data: queue, isLoading: isLoadingQueue } = useCaseQueue(queueId);
  const { data: version, isLoading: isLoadingTemplateVersion } =
    useCaseTemplateVersion({
      templateId: templateId,
      versionId: versionId,
    });

  const isLoading = isLoadingQueue || isLoadingTemplateVersion;

  const status = useMemo(() => {
    if (!queue) return;

    const isActiveTemplate = queue?.template_configs?.some(
      (config) => config.template.id === templateId,
    );

    if (!isActiveTemplate) return TemplateStatus.Deleted;

    const isOutdatedTemplateVersion =
      version?.attributes?.schema_version !== undefined &&
      schemaVersion !== undefined &&
      version.attributes.schema_version > schemaVersion;

    if (isOutdatedTemplateVersion) return TemplateStatus.Outdated;

    return TemplateStatus.Active;
  }, [version, schemaVersion, templateId, queue]);

  return {
    isLoading,
    status,
  };
};

export const useRefreshQueue = () => {
  const queryClient = useQueryClient();
  const { queue_id } = useParamsDecode<CasesPageParams>();
  const { workspace } = useWorkspaceContext();

  const isFetching = useIsFetching({
    predicate: ({ queryKey }) =>
      queryKey.includes(CASE_QUEUES_QUERY_KEY_PREFIX) &&
      queryKey.includes(workspace.id) &&
      queryKey.includes(queue_id),
    exact: false,
  });

  const refreshQueue = async () => {
    if (isFetching) return;

    await queryClient.invalidateQueries({
      queryKey: caseQueuesKeys.get(workspace.id, queue_id),
    });
  };

  return { refreshQueue, isRefreshingQueue: !!isFetching };
};

type UseIsQueueMemberParams = {
  userId?: string;
  queueId?: string;
};

export const useIsQueueMember = ({
  userId,
  queueId,
}: UseIsQueueMemberParams) => {
  const { data: members } = useQueueMembers(queueId);
  return members?.some((member) => member.id === userId) ?? false;
};

export const deserializeCaseTemplates = (data: CaseTemplatesResponse) =>
  deserializeJsonApi<DeserializedCaseTemplateWithVersionsData>(data);

export const useQueueCaseTemplates = (queueId?: string) => {
  const { data: caseQueue, isLoading: isLoadingQueue } = useCaseQueue(queueId);
  const { data: caseTemplates, isLoading: isLoadingTemplates } =
    useCaseTemplatesRaw({
      select: deserializeCaseTemplates,
    });

  const queueCaseTemplates = useMemo(() => {
    if (!caseTemplates || !caseQueue) return [];

    const templateIds = caseQueue?.template_configs?.map(
      (config) => config.template.id,
    );

    return caseTemplates?.data?.filter((template) =>
      templateIds?.includes(template.id),
    );
  }, [caseTemplates, caseQueue]);

  return {
    data: queueCaseTemplates,
    isLoading: isLoadingQueue || isLoadingTemplates,
  };
};
